import React, { createContext, useContext } from "react";
import router from "next/router";
import { checkIsEmptyHtmlString } from "repoV2/utils/common/validators/validators";
import { getPreviewableValue } from "repoV2/utils/previewPages";
import { appendURLSearchParams } from "repoV2/utils/urls&routing/urlParams";
import { URL_SEARCH_PARAMS } from "repoV2/constants/urls&routing/urlParams";
import { APP_ROUTE_KEYS } from "repoV2/constants/urls&routing/routing";
import { constructRoute } from "repoV2/utils/urls&routing/routing";
import { useSalePageContext } from "../SalePageContext";

type IContactCreatorSectionContextValue = {
    hide: boolean;
    title: string;
    description: string;
    getInTouchCtaText: string;
    onGetInTouchClick: () => void;
};

const initialContextValue: IContactCreatorSectionContextValue = {
    hide: false,
    title: "",
    description: "",
    getInTouchCtaText: "",
    onGetInTouchClick: () => {},
};

const ContactCreatorSectionContext = createContext(initialContextValue);

export function ContactCreatorSectionContextProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { sectionsData, isPreview } = useSalePageContext();
    const title = getPreviewableValue({
        value: sectionsData.contactCreatorSection.title,
        dummyString: "&lt;Have any questions?&gt;",
        isPreview,
    });
    const description = getPreviewableValue({
        value: sectionsData.contactCreatorSection.description,
        dummyString:
            "&lt;Give us your details and we’ll get in touch with you.&gt;",
        isPreview,
    });
    const getInTouchCtaText =
        sectionsData.contactCreatorSection.getInTouchCtaText ||
        (isPreview ? "Get in touch" : "");
    const { getInTouchCustomCtaUrl } = sectionsData.contactCreatorSection;

    const contextValue: IContactCreatorSectionContextValue = {
        hide:
            checkIsEmptyHtmlString(title) &&
            checkIsEmptyHtmlString(description) &&
            !getInTouchCtaText,
        title,
        description,
        getInTouchCtaText,
        onGetInTouchClick: () => {
            if (isPreview) return;

            let url = "";
            if (getInTouchCustomCtaUrl) {
                url = getInTouchCustomCtaUrl;
                // to pass on the UTM params in the current url
                url = appendURLSearchParams(url, { ...router?.router?.query });
            } else {
                url = appendURLSearchParams(
                    constructRoute({
                        appRouteKey: APP_ROUTE_KEYS.HOME,
                    }),
                    {
                        ...router?.router?.query,
                        [URL_SEARCH_PARAMS.INIT_CONTACT]: true,
                        [URL_SEARCH_PARAMS.CWACCFSS]: true,
                    }
                );
            }
            window.open(url, "_blank");
        },
    };
    return (
        <ContactCreatorSectionContext.Provider value={contextValue}>
            {children}
        </ContactCreatorSectionContext.Provider>
    );
}

export function useContactCreatorSectionContext() {
    return useContext(ContactCreatorSectionContext);
}
